import { FC, useMemo, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight, MapPin, Package, Truck } from "lucide-react";
import { capitalize } from "~/utils";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { JobStatus, ServiceType } from "~/utils/constants";
import { useNavigate } from "react-router-dom";

dayjs.extend(isoWeek);

interface ServiceRow {
  id: string;
  serviceDatetime: string;
  serial: string;
  project: string;
  serviceType: ServiceType;
  bookingRef: string;
  location: string;
  status: JobStatus;
  rawBooking: any;
  rawService: any;
}

type Props = {
  services: ServiceRow[];
  onServiceClick?: (service: ServiceRow) => void;
};

const getServiceTypeIcon = (type: ServiceType) => {
  switch (type) {
    case ServiceType.DELIVERY:
      return <Truck size={12} />;
    case ServiceType.COLLECTION:
      return <Package size={12} />;
    default:
      return <Calendar size={12} />;
  }
};

const getStatusColor = (status: JobStatus) => {
  const statusMap: Record<string, string> = {
    [JobStatus.COMPLETED]: "success",
    [JobStatus.IN_PROGRESS]: "processing",
    [JobStatus.SCHEDULED]: "blue",
    [JobStatus.DRAFT]: "default",
    [JobStatus.CANCELLED]: "error",
    [JobStatus.ESTIMATED]: "warning",
  };
  return statusMap[status] || "default";
};

export const ServicesCalendarView: FC<Props> = ({ services, onServiceClick }) => {
  const navigate = useNavigate();
  const [currentWeekStart, setCurrentWeekStart] = useState(dayjs().startOf("isoWeek"));

  // Group services by date
  const servicesByDate = useMemo(() => {
    const grouped: Record<string, ServiceRow[]> = {};

    services.forEach((service) => {
      const date = dayjs(service.serviceDatetime).format("YYYY-MM-DD");
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(service);
    });

    // Sort services within each date by time
    Object.keys(grouped).forEach((date) => {
      grouped[date].sort((a, b) => {
        // Use the same logic as display to get accurate time for sorting
        const getDisplayTime = (service: ServiceRow) => {
          const serviceDatetime = dayjs(service.serviceDatetime);
          const rawService = service.rawService;
          const serviceDatetimeUTC = serviceDatetime.utc();
          const serviceDatetimeLocal = serviceDatetime.local();
          const isNoonUTCPlaceholder = serviceDatetimeUTC.hour() === 12 && serviceDatetimeUTC.minute() === 0;
          const isMidnightLocal = serviceDatetimeLocal.hour() === 0 && serviceDatetimeLocal.minute() === 0;
          
          if (rawService?.startDatetime && (isNoonUTCPlaceholder || isMidnightLocal)) {
            const startTimeData = dayjs(rawService.startDatetime);
            return serviceDatetime
              .hour(startTimeData.hour())
              .minute(startTimeData.minute())
              .second(startTimeData.second());
          }
          return serviceDatetime;
        };
        
        const timeA = getDisplayTime(a);
        const timeB = getDisplayTime(b);
        return timeA.diff(timeB);
      });
    });

    return grouped;
  }, [services]);

  const navigateWeeks = (direction: "prev" | "next") => {
    if (direction === "prev") {
      setCurrentWeekStart(currentWeekStart.subtract(2, "week"));
    } else {
      setCurrentWeekStart(currentWeekStart.add(2, "week"));
    }
  };

  const goToToday = () => {
    setCurrentWeekStart(dayjs().startOf("isoWeek"));
  };

  // Generate 14 days starting from currentWeekStart
  const twoWeekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 14; i++) {
      days.push(currentWeekStart.add(i, "day"));
    }
    return days;
  }, [currentWeekStart]);

  const endDate = currentWeekStart.add(13, "day");

  const handleServiceClick = (service: ServiceRow) => {
    if (onServiceClick) {
      onServiceClick(service);
    } else {
      navigate(`/bookings/${service.rawBooking.id}?tab=services&serviceId=${service.id}`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border border-[#e5e5e5] rounded-lg p-4 bg-white">
        <div className="flex items-center gap-3">
          <Calendar className="text-[#1a1a2e]" size={24} />
          <div>
            <h3 className="text-lg font-bold uppercase tracking-wide">Calendar View</h3>
            <p className="text-sm font-medium text-[#1a1a2e]">
              {currentWeekStart.format("MMM D")} - {endDate.format("MMM D, YYYY")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigateWeeks("prev")}
            className="border border-[#e5e5e5] rounded-lg px-4 py-2 font-bold text-sm uppercase bg-white hover:bg-gray-100"
          >
            <ChevronLeft size={16} className="inline" />
          </button>
          <button
            onClick={goToToday}
            className="border border-[#e5e5e5] rounded-lg px-4 py-2 font-bold text-sm uppercase bg-[#1a1a2e] text-white"
          >
            Today
          </button>
          <button
            onClick={() => navigateWeeks("next")}
            className="border border-[#e5e5e5] rounded-lg px-4 py-2 font-bold text-sm uppercase bg-white hover:bg-gray-100"
          >
            <ChevronRight size={16} className="inline" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2">
        {twoWeekDays.map((day) => {
          const dateKey = day.format("YYYY-MM-DD");
          const dayServices = servicesByDate[dateKey] || [];
          const isToday = day.isSame(dayjs(), "day");
          const isPast = day.isBefore(dayjs(), "day");

          return (
            <div
              key={dateKey}
              className={`bg-white border border-[#e5e5e5] rounded-lg p-2 min-h-[200px] ${
                isPast ? "bg-gray-100" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wide text-[#1a1a2e]">
                    {day.format("ddd")}
                  </div>
                  <div
                    className={`text-xl font-bold ${isPast ? "text-gray-400" : "text-[#1a1a2e]"}`}
                  >
                    {day.format("D")}
                  </div>
                </div>
                {dayServices.length > 0 && (
                  <div className="text-xs font-bold uppercase border border-[#e5e5e5] rounded-lg px-2 py-1 bg-[#1a1a2e] text-white">
                    {dayServices.length}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                {dayServices.length === 0 ? (
                  <div className="text-xs text-center py-4 text-gray-400">No services</div>
                ) : (
                  dayServices.map((service) => {
                    // Read time from servicedatetime, but if servicedatetime is stored as a placeholder
                    // (noon UTC for date-only or midnight), use startDatetime for the time portion
                    let displayTime;
                    const serviceDatetime = dayjs(service.serviceDatetime);
                    const rawService = service.rawService;
                    
                    // Check if servicedatetime has meaningful time
                    // - If hour is 12 and minute is 0 in UTC, it's likely a date-only placeholder (noon UTC)
                    // - If hour is 0 and minute is 0, it might be midnight (also a placeholder)
                    const serviceDatetimeUTC = serviceDatetime.utc();
                    const serviceDatetimeLocal = serviceDatetime.local();
                    const isNoonUTCPlaceholder = serviceDatetimeUTC.hour() === 12 && serviceDatetimeUTC.minute() === 0;
                    const isMidnightLocal = serviceDatetimeLocal.hour() === 0 && serviceDatetimeLocal.minute() === 0;
                    
                    // If servicedatetime appears to be a placeholder and we have startDatetime, use startDatetime for time
                    if (rawService?.startDatetime && (isNoonUTCPlaceholder || isMidnightLocal)) {
                      // Combine date from serviceDatetime with time from startDatetime
                      const startTimeData = dayjs(rawService.startDatetime);
                      displayTime = serviceDatetime
                        .hour(startTimeData.hour())
                        .minute(startTimeData.minute())
                        .second(startTimeData.second());
                    } else {
                      // Use servicedatetime directly (it should have the correct time)
                      displayTime = serviceDatetime;
                    }

                    return (
                      <div
                        key={service.id}
                        className="border border-[#e5e5e5] rounded-lg p-2 cursor-pointer hover:bg-gray-100 bg-white"
                        onClick={() => handleServiceClick(service)}
                      >
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold uppercase">{service.serial}</span>
                            <span className={`inline-block px-1 py-0.5 text-xs font-bold uppercase border border-[#e5e5e5] rounded-lg ${
                              service.status === JobStatus.COMPLETED || service.status === JobStatus.SCHEDULED || service.status === JobStatus.IN_PROGRESS
                                ? "bg-[#1a1a2e] text-white"
                                : "bg-gray-200 text-[#1a1a2e]"
                            }`}>
                              {capitalize(service.status || "pending")}
                            </span>
                          </div>

                          <div className="flex items-center gap-1 text-xs font-medium">
                            {getServiceTypeIcon(service.serviceType)}
                            <span>{capitalize(service.serviceType)}</span>
                          </div>

                          <div className="text-xs font-bold">
                            {displayTime.format("h:mm A")}
                          </div>

                          {service.location && service.location !== "-" && (
                            <div className="flex items-center gap-1 text-xs">
                              <MapPin size={10} />
                              <span className="truncate">{service.location}</span>
                            </div>
                          )}

                          {service.project && service.project !== "-" && (
                            <div className="text-xs truncate">
                              {service.project}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

